import { useState } from "react";
import { toast } from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";
import { API_URLS } from "../../config/APIUrls";
import axiosInstance from "../../config/axios";
import Loader from "../../Shared/Loader";
import { buildInitialSectionValues, MATERIAL_SECTIONS, WORK_TYPES } from "./materialSections";

const inputClass =
  "w-full rounded-xl px-3 py-2 text-sm outline-none border transition-colors";

const inputStyle = {
  background: "var(--input-bg)",
  borderColor: "var(--border)",
  color: "var(--text-main)",
};

const labelStyle = {
  color: "var(--text-main)",
  fontWeight: 600,
  fontSize: "0.85rem",
  marginBottom: "6px",
  display: "block",
};

const CreateMaterialEntry = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const existingEntry = location.state?.entry || null;
  const isEdit = Boolean(existingEntry?.id);

  const [loading, setLoading] = useState(false);

  const [project, setProject] = useState({
    crm_project_name: existingEntry?.crm_project_name || "",
    crm_client: existingEntry?.crm_client || "",
    crm_location: existingEntry?.crm_location || "",
    crm_date: existingEntry?.crm_date ? existingEntry.crm_date.substring(0, 10) : "",
    crm_site_engineer: existingEntry?.crm_site_engineer || "",
    crm_work_type: existingEntry?.crm_work_type || WORK_TYPES[0],
    crm_remark: existingEntry?.crm_remark || "",
  });

  const [sections, setSections] = useState(() => {
    const initial = buildInitialSectionValues();
    if (existingEntry?.material_data) {
      Object.keys(existingEntry.material_data).forEach((sectionId) => {
        if (initial[sectionId]) {
          initial[sectionId] = { ...initial[sectionId], ...existingEntry.material_data[sectionId] };
        }
      });
    }
    return initial;
  });

  const updateProject = (key, value) => {
    setProject((prev) => ({ ...prev, [key]: value }));
  };

  const updateSectionField = (sectionId, key, value) => {
    setSections((prev) => ({
      ...prev,
      [sectionId]: { ...prev[sectionId], [key]: value },
    }));
  };

  const handleSave = async (status) => {
    if (!project.crm_project_name.trim()) {
      toast.error("Project Name is required");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        ...(isEdit ? { id: existingEntry.id } : {}),
        ...project,
        crm_status: status,
        material_data: sections,
      };

      const res = await axiosInstance.post(API_URLS.create_material_entry, payload);

      toast(res.data.msg || res.data.message, { id: 1 });

      if (res.data.success) {
        navigate("/material-entries");
      }
    } catch (e) {
      console.error(e);
      toast.error("Something went wrong");
    }

    setLoading(false);
  };

  return (
    <div className="p-3 sm:p-4 lg:p-6 w-full overflow-x-hidden" style={{ minHeight: "100%" }}>
      <Loader isLoading={loading} />

      {/* <div
        className="rounded-2xl p-6 mb-5"
        style={{
          background: "linear-gradient(120deg, var(--primary) 0%, #b27d34 60%, var(--primary-hover) 100%)",
        }}
      >
        <span
          className="inline-block px-3 py-1 rounded-full text-xs font-semibold mb-2"
          style={{ background: "rgba(255,255,255,0.2)", color: "#fff" }}
        >
          Builder & Developer Portal
        </span>
        <h1 className="text-2xl font-bold text-white mb-1">
          {isEdit ? "Update Material Entry" : "Material Entry Dashboard"}
        </h1>
        <p className="text-sm" style={{ color: "rgba(255,255,255,0.9)" }}>
          A clean, practical data-entry experience for project managers, site engineers, and procurement teams.
        </p>
      </div> */}
   <div className="text-2xl text-white font-bold mb-4" style={{ color: "var(--text-main)" }}>
    Material Entry 
   </div>
      <div
        className="rounded-2xl p-3 sm:p-5"
        style={{ background: "var(--card-bg)", border: "1px solid var(--border)" }}
      >
        {/* Section 01 - Project Overview */}
        <div className="rounded-xl p-3 sm:p-4 mb-4" style={{ border: "1px solid var(--border)" }}>
          <SectionTitle number="01" title="Project Overview" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label style={labelStyle}>Project Name</label>
              <input
                className={inputClass}
                style={inputStyle}
                placeholder="e.g. Sunrise Villa, Block A"
                value={project.crm_project_name}
                onChange={(e) => updateProject("crm_project_name", e.target.value)}
              />
            </div>
            <div>
              <label style={labelStyle}>Client</label>
              <input
                className={inputClass}
                style={inputStyle}
                placeholder="Client name"
                value={project.crm_client}
                onChange={(e) => updateProject("crm_client", e.target.value)}
              />
            </div>
            <div>
              <label style={labelStyle}>Location</label>
              <input
                className={inputClass}
                style={inputStyle}
                placeholder="Site location"
                value={project.crm_location}
                onChange={(e) => updateProject("crm_location", e.target.value)}
              />
            </div>
            <div>
              <label style={labelStyle}>Date</label>
              <input
                type="date"
                className={inputClass}
                style={inputStyle}
                value={project.crm_date}
                onChange={(e) => updateProject("crm_date", e.target.value)}
              />
            </div>
            <div>
              <label style={labelStyle}>Site Engineer</label>
              <input
                className={inputClass}
                style={inputStyle}
                placeholder="Name of engineer"
                value={project.crm_site_engineer}
                onChange={(e) => updateProject("crm_site_engineer", e.target.value)}
              />
            </div>
            <div>
              <label style={labelStyle}>Work Type</label>
              <select
                className={inputClass}
                style={inputStyle}
                value={project.crm_work_type}
                onChange={(e) => updateProject("crm_work_type", e.target.value)}
              >
                {WORK_TYPES.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label style={labelStyle}>Remark</label>
              <textarea
                rows={2}
                className={inputClass}
                style={inputStyle}
                placeholder="Any remark for this section"
                value={project.crm_remark}
                onChange={(e) => updateProject("crm_remark", e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Sections 02-11 */}
        {MATERIAL_SECTIONS.map((section) => (
          <div key={section.id} className="rounded-xl p-3 sm:p-4 mb-4" style={{ border: "1px solid var(--border)" }}>
            <SectionTitle number={section.id} title={section.title} />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {section.fields.map((field) => (
                <div key={field.key} className="min-w-0">
                  <label style={labelStyle}>{field.label}</label>
                  <div className="flex flex-col sm:flex-row gap-2 sm:gap-1">
                    <input
                      type={field.inputType === "text" ? "text" : "number"}
                      className={`${inputClass} w-full sm:flex-1 sm:min-w-0`}
                      style={inputStyle}
                      placeholder={field.placeholder}
                      value={sections[section.id]?.[field.key] ?? ""}
                      onChange={(e) => updateSectionField(section.id, field.key, e.target.value)}
                    />
                    {field.extra && (
                      <input
                        type="text"
                        className={`${inputClass} w-full sm:flex-1 sm:min-w-0`}
                        style={inputStyle}
                        placeholder={field.extra.placeholder}
                        value={sections[section.id]?.[field.extra.key] ?? ""}
                        onChange={(e) => updateSectionField(section.id, field.extra.key, e.target.value)}
                      />
                    )}
                    {field.unit.type === "fixed" ? (
                      <span
                        className="flex items-center justify-center px-3 py-2 sm:py-0 rounded-xl text-xs font-semibold whitespace-nowrap w-full sm:w-auto sm:min-w-[70px] shrink-0"
                        style={{ background: "var(--input-bg)", border: "1px solid var(--border)", color: "var(--primary)" }}
                      >
                        {field.unit.value}
                      </span>
                    ) : (
                      <select
                        className={`${inputClass} w-full sm:w-auto sm:min-w-[90px] sm:max-w-[110px] shrink-0`}
                        style={inputStyle}
                        value={sections[section.id]?.[`${field.key}_unit`] ?? field.unit.options[0]}
                        onChange={(e) => updateSectionField(section.id, `${field.key}_unit`, e.target.value)}
                      >
                        {field.unit.options.map((opt) => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4">
              <label style={labelStyle}>Remark</label>
              <textarea
                rows={2}
                className={inputClass}
                style={inputStyle}
                placeholder="Any remark for this section"
                value={sections[section.id]?.remark ?? ""}
                onChange={(e) => updateSectionField(section.id, "remark", e.target.value)}
              />
            </div>
          </div>
        ))}

        <div className="flex flex-wrap gap-3 mt-2">
          <button
            type="button"
            onClick={() => handleSave("Draft")}
            className="px-5 py-2.5 rounded-full font-semibold text-sm"
            style={{ border: "1px solid var(--primary)", color: "var(--primary)", background: "transparent" }}
          >
            Save Draft
          </button>
          <button
            type="button"
            onClick={() => handleSave("Submitted")}
            className="px-5 py-2.5 rounded-full font-semibold text-sm"
            style={{ background: "var(--primary)", color: "#000" }}
          >
            Submit
          </button>
        </div>
      </div>
    </div>
  );
};

const SectionTitle = ({ number, title }) => (
  <div className="flex items-center gap-3 mb-4">
    <span
      className="flex items-center justify-center rounded-lg font-bold text-xs"
      style={{ width: 32, height: 32, background: "var(--primary)", color: "#000" }}
    >
      {number}
    </span>
    <span className="font-bold" style={{ color: "var(--text-main)" }}>{title}</span>
  </div>
);

export default CreateMaterialEntry;
